import { registerActionHandler } from '../action-registry';
import { buildSmallAppUrl } from '../smallapp-url';
import { getSmallAppRestToken } from '../get-smallapp-rest-token';
import { getNested } from '../nested-keys';

/**
 * `checkPublic` — the Google Slides paste-first public/private probe + webLink/folder normalizer.
 * Ported from the legacy remote form (optisigns-smallapp-frontend-google-slides-remote
 * `form.tsx` + `asset.types.ts`): on Resolve it derives `folder.id` from the pasted link and composes
 * the on-device `webLink`, exactly as the legacy did client-side (the REST just stores what it gets).
 *
 * Flow (mirrors the legacy checkPub handler):
 *  - PUBLISHED URL (`/d/e/2PACX-…/pub|embed`) → ALWAYS public; `folder.id` = the 2PACX id; force
 *    `/pub→/embed` + embed params. Skips the API.
 *  - REGULAR URL → `/asset/checkPub` API →
 *      public  → rebuild to `/embed` ONLY if not already `/embed` and not published, then embed params.
 *      private → `/edit→/embed` (renders via the converted `doc_pages`, not the iframe).
 *  - `folder.id` = the presentation id, set from the URL as the legacy did (before the API call).
 *
 * `webLink` is composed with `setParamsQuery` (preserves the pasted path; sets rm/start/loop/delayms),
 * `delayms = durationPage*1000` — verbatim from the legacy. Fail-soft: bad id / no token / unreachable
 * / non-ok / non-JSON leaves `isPublic` at its default (false → private) and never blocks navigation.
 */

// ── Legacy remote parity (asset.types.ts) ──────────────────────────────────────────────────────
const PRESENTATION_ID_RE = /\/d\/([a-zA-Z0-9-_]+)/; // extractPresentationId
const PUBLISHED_RE = /^https:\/\/docs\.google\.com\/presentation\/d\/e\/2PACX-[\w-]+\/(pub|embed)/;
const PUBLISHED_ID_RE = /\/d\/e\/(2PACX-[\w-]+)/;

const extractPresentationId = (url: string): string => url.match(PRESENTATION_ID_RE)?.[1] ?? '';

/** setParamsQuery — verbatim port: preserve the URL path, delete+append each param, fall back on error. */
const setParamsQuery = (
  url: string,
  params: Array<{ key: string; value: string | number | boolean }>,
): string => {
  try {
    const u = new URL(url);
    for (const { key, value } of params) {
      u.searchParams.delete(key);
      u.searchParams.append(key, String(value));
    }
    return u.toString();
  } catch {
    return url;
  }
};

const embedParams = (durationPage: unknown): Array<{ key: string; value: string | number }> => {
  const dp = Number(durationPage);
  const delayms = (Number.isFinite(dp) && dp >= 1 ? dp : 5) * 1000; // legacy default 5s
  return [
    { key: 'rm', value: 'minimal' },
    { key: 'start', value: 'true' },
    { key: 'loop', value: 'true' },
    { key: 'delayms', value: delayms },
  ];
};

// DEV FAKE — forces `isPublic` so BOTH branches are testable before `/checkPub` is reachable.
// `true` → public, `false` → private, `null` → call the real endpoint. Tests override via `__setFakeIsPublic`.
let fakeIsPublic: boolean | null = null;
/** Test/dev override for the DEV FAKE above. */
export function __setFakeIsPublic(v: boolean | null): void {
  fakeIsPublic = v;
}

registerActionHandler('checkPublic', async ({ values, form, appMeta, field, apolloClient }) => {
  const url = String(getNested(values, field.key) ?? '').trim();
  const durationPage = getNested(values, 'meta.durationPage');

  const set = (name: string, value: unknown): void =>
    form.setValue(name, value, { shouldValidate: true, shouldDirty: true });
  const setFolderId = (id: string | undefined): void => {
    if (id) set('folder.id', id);
  };
  const setPublicWebLink = (baseUrl: string): void =>
    set('webLink', setParamsQuery(baseUrl, embedParams(durationPage)));

  // PUBLISHED URLs are ALWAYS public — handle first, skip the API (legacy form.tsx).
  if (PUBLISHED_RE.test(url)) {
    setFolderId(url.match(PUBLISHED_ID_RE)?.[1]);
    setPublicWebLink(url.replace(/\/(pub|embed)/, '/embed'));
    set('isPublic', true);
    return;
  }

  const presentationId = extractPresentationId(url);

  // Regular-URL public compose: rebuild to /embed only if not already /embed and not published.
  const composePublic = (): void =>
    setPublicWebLink(
      !url.includes('/embed') && !url.includes('/d/e/')
        ? `https://docs.google.com/presentation/d/${presentationId}/embed`
        : url,
    );
  // Private compose: /edit → /embed (legacy).
  const composePrivate = (): void => set('webLink', url.replace(/\/edit/, '/embed'));

  // DEV FAKE short-circuit.
  if (fakeIsPublic !== null) {
    setFolderId(presentationId);
    set('isPublic', fakeIsPublic);
    if (presentationId) (fakeIsPublic ? composePublic : composePrivate)();
    return;
  }

  const apiUrl = appMeta?.apiUrl;
  // No parseable deck id or app not configured → leave isPublic at its default (private).
  if (!presentationId || !apiUrl) return;

  // folder.id from the pasted URL (the legacy sets it before the API check).
  setFolderId(presentationId);

  // Firebase/share-link REST token the microservice accepts (the open-api proxy 403s + needs a saved asset).
  const token = await getSmallAppRestToken(apolloClient);

  let response: Response;
  try {
    response = await fetch(buildSmallAppUrl(apiUrl, 'asset/checkPub'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ presentationId }),
    });
  } catch {
    return; // unreachable → private fallback
  }
  if (!response.ok) return;

  let data: { data?: { isPublic?: boolean } } | null = null;
  try {
    data = (await response.json()) as { data?: { isPublic?: boolean } };
  } catch {
    return; // non-JSON body → private fallback
  }

  const isPublic = !!data?.data?.isPublic;
  set('isPublic', isPublic);
  (isPublic ? composePublic : composePrivate)();
});
